// @ts-strict-ignore
import * as React from 'react';
import {clsx} from 'clsx';

import {ButtonReact} from 'common/components/button';
import {ButtonSize, ButtonTheme, ButtonType} from 'common/components/button/constants';
import {Section} from 'common/components/items-menu/types';
import {areSectionsEmpty} from 'common/components/items-menu/utils';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {QueryBlockReact} from 'common/query-builder/query-block';
import {MetricType, SelectorType} from 'common/query-builder/query-block/constants';
import {
  generateCountCalculationMenuOptions,
  getSelectedCountBy,
  isAdvancedValueCalculation,
} from 'common/query-builder/query-block/count-menu/util';
import {
  PropertySectionItem,
  SectionItemProperty,
} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {
  getMultiAttributionFromEntry,
  isSupportedMultiAttributionTypeForEntry,
  qbStyleAttrs,
} from 'common/query-builder/query-builder/util';
import {
  COUNT_USERS_ONCE_TYPE,
  CountUsersOnceState,
  MATH_TYPE,
  PRIMITIVE_MATH_TYPES,
  ResourceType,
} from 'common/report/constants';
import {getIconForPropertyType, isScdPropertyName} from 'common/report/util/properties';
import {instrumentWithTimer} from 'common/util/workflow-timing';
import {MultiAttributionMenu} from 'common/widgets/multi-attribution-menu';
import {DefaultMultiAttribution} from 'common/widgets/multi-attribution-menu/constants';
import {preventTracking} from 'common/util/dom';
import {MenuType} from 'common/query-builder/query-block/types';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {SelectedPropertyItem} from 'common/query-builder/query-block/properties-menu/types';
import {useTranslation} from 'common/i18n/hooks';
import {FeatureRolloutName} from 'common/report/util/feature-sampling';
import {useFeatureRollout} from 'common/hooks/useFeatureRollout';
import {RECURRING_REVENUE_CHANGE} from 'common/query-builder/query-block/properties-menu/constants';
import {CountQueryBlock} from 'common/query-builder/query-block/count-menu/count-query-block';

import {
  Action,
  Calculation,
  CalculationTypes,
  Entry,
  EntryPart,
  InsightsCountType,
  isEventBasedEntry,
  isFormulaEntry,
  isMetricEntry,
  isQueryUserEntry,
  isUserBasedEntry,
  QueryEntry,
  QueryMetricEntry,
  SelectorTypeAggregation,
} from '../types';
import {
  getQueryEntryUpdateFromCalculation,
  getSelectedCalculation,
  getSelectedCalculationLabel,
  isProfileScdAggregation,
} from '../util';
import {EntryRowProps} from '../row-types/types';
import {
  getMultiAttributionLabel,
  getMultiAttributionTitle,
  getUpdatedMultiAttribution,
} from '../util/multi-attribution';
import {TrackingEvent} from '../tracking';

/**
 * Returns an aggregator function for a given selector type aggregation
 */
function getSelectorTypeDefaultAggregator(type: SelectorTypeAggregation): MATH_TYPE {
  return type.subAggregations ? type.subAggregations[0].operator : type.operator;
}

/**
 * Updates aggregation function on an entry
 */
function onEntryAggregationFunctionSelected(
  props: EntryRowProps,
  aggregation: SelectorTypeAggregation,
  isSubAggregation = false,
) {
  const sourceEntry = props.entry as QueryMetricEntry;

  // remove property selection when changing to a non-aggregation type
  const keepProperty = aggregation.forProperty;

  // attempts to preserve the current aggregationOperator if it's available on the selected aggregation
  // prevent `Distinct Count by Sum` from transitioning
  const transitionToUniqueValuesPerUser =
    aggregation.perUserOperator === PRIMITIVE_MATH_TYPES.UNIQUE_VALUES &&
    sourceEntry.aggregationOperator === PRIMITIVE_MATH_TYPES.TOTAL;
  const keepAggregationOperator =
    !transitionToUniqueValuesPerUser && !isSubAggregation && aggregation.subAggregations
      ? aggregation.subAggregations.some(
          (subAggregation) => subAggregation.operator === sourceEntry.aggregationOperator,
        )
      : false;

  const entry: QueryMetricEntry = {
    ...sourceEntry,
    // remove property selection when changing to a non-aggregation type
    property: keepProperty ? sourceEntry.property ?? ({value: null} as PropertySectionItem) : null,

    aggregationOperator: keepAggregationOperator
      ? sourceEntry.aggregationOperator
      : getSelectorTypeDefaultAggregator(aggregation),

    aggregationOperatorPerUser: aggregation.perUserOperator || null,
  };

  props.updateEntry(Action.Edit, entry);

  // focus on numeric property field when selecting a numeric property aggregation
  if (
    !isFormulaEntry(entry) &&
    Boolean(entry.event?.value) &&
    aggregation.forProperty &&
    entry.property?.value === null
  ) {
    props.setActivePart(EntryPart.NumericProperty, `open`);
  }

  props.dispatchTrack(TrackingEvent.SelectNumericAggregator, {
    ...props.getEntryTrackingProps(entry),
    Aggregator: entry.aggregationOperator,
    'Per-user aggregator': entry.aggregationOperatorPerUser,
  });
}

/**
 * Defines the 1st pass aggregation function
 */
export function RowNumericAggregator(props: EntryRowProps) {
  const {print} = useTranslation();

  const sections = props.getSelectorAggregationsForEntry();
  if (!sections.length) {
    return null;
  }

  return (
    <div className='mp-query-entry-aggregator'>
      <SelectReact
        key='entry-aggregator'
        elref='aggregatorSelect'
        alwaysShowCaret
        selectedOption={print(
          props.getSelectedAggregationData({entry: props.entry as QueryMetricEntry}).firstPassAggregationLabel,
        )}
        disableSearch
        menuWidth={-1}
        menuHeight={0}
        theme={props.hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select}
        icon={props.hasNewUI && {rightIcon: `double-caret`}}
        trackName='operator'
        onChange={instrumentWithTimer((ev: CustomEvent) =>
          onEntryAggregationFunctionSelected(props, ev.detail.item.value),
        )}
        onMenuChange={(ev) => props.onSelectMenuChange(ev, EntryPart.Aggregator)}
        sections={sections}
      />
    </div>
  );
}

/**
 * Defines the 2nd pass aggregation for a given metric
 *
 * For instance, if working on a per-user level aggregation (event count, property aggregation), aa 2-nd level
 * aggregation can be "from all of the distinct counts per-user, aggregate all on a distribution" distribution
 * in this example is the 2nd pass aggregation
 */
export function RowNumericSubAggregator(props: EntryRowProps<QueryMetricEntry>) {
  const {print} = useTranslation();

  const generateSectionsFromAggregations = (aggregations: SelectorTypeAggregation[]): Section[] => {
    if (!aggregations) {
      return [];
    }

    const items = aggregations.map((aggregation) => props.generateAggregationSectionItem(aggregation));
    return [{items}];
  };

  const aggregatorEl = (trackName: string, selectedOption: string, sections: Section[], label = null) =>
    !areSectionsEmpty(sections) && (
      <>
        {!props.hasNewUI && `Aggregate by`}
        <SelectReact
          key='entry-subaggregator'
          elref='subAggregatorSelect'
          {...(label ? {label} : null)}
          selectedOption={selectedOption}
          disableSearch
          menuWidth={0}
          menuHeight={340}
          trackName={trackName}
          theme={props.hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select}
          tooltip={{placement: props.hasNewUI ? Placement.Bottom : Placement.Top}}
          onChange={(ev: CustomEvent) => onEntryAggregationFunctionSelected(props, ev.detail.item.value, true)}
          onMenuChange={(ev) => props.onSelectMenuChange(ev, EntryPart.SubAggregator)}
          sections={sections}
        />
      </>
    );

  const {firstPassAggregation, firstPassAggregationLabel, secondPassAggregationLabel} =
    props.getSelectedAggregationData({
      entry: props.entry,
      forNewWorkflow: true,
    });
  const firstPassAggregations = props.getSelectorAggregationsForEntry();
  const secondPassAggregations = generateSectionsFromAggregations(firstPassAggregation.subAggregations);

  if (!firstPassAggregations.length && !secondPassAggregations.length) {
    return null;
  }

  return (
    <div className='mp-query-entry-subaggregator'>
      {/* 1st-pass operator (Distinct Count per user) */}
      {props.hasNewUI && aggregatorEl(`1-pass`, print(firstPassAggregationLabel), firstPassAggregations)}
      {/* 2-pass operator (by Distribution) */}
      {aggregatorEl(
        `2-pass`,
        print(secondPassAggregationLabel),
        secondPassAggregations,
        props.hasNewUI && `by ${secondPassAggregationLabel}`,
      )}
    </div>
  );
}

export function CountBySelection(props: EntryRowProps<Entry>) {
  const isRevenueEnabled = useFeatureRollout(FeatureRolloutName.Revenue);
  const {t, print} = useTranslation();
  const dateUnit = props.sectionProps.dateUnit;
  const plotStyle = props.sectionProps.plotStyle;
  const isUserBased = isUserBasedEntry(props.entry);
  const dataGroupId = isMetricEntry(props.entry) ? props.entry.behavior.dataGroupId : props.entry.dataGroupId;
  const selectedCalculation = getSelectedCalculation(props.entry as QueryMetricEntry);
  const selectedCalculationLabel = getSelectedCalculationLabel(selectedCalculation, dateUnit);
  const calculationSelectorOptions =
    isUserBased && !isProfileScdAggregation(props.entry)
      ? []
      : generateCountCalculationMenuOptions({
          selectedPlotStyle: plotStyle,
          selectedCalculation,
          selectedDateUnit: dateUnit,
        });
  const queryMetricEntry = props.entry as QueryMetricEntry;
  return (
    <>
      <CountQueryBlock
        className='subitem-style'
        elref='countBlock'
        metricType={isUserBased ? MetricType.People : MetricType.Event}
        open={props.activePart === EntryPart.SubAggregator}
        advancedMenuSections={calculationSelectorOptions}
        allDataGroups={props.sectionProps.dataGroupsEnabled}
        countUsersOnceState={
          getMultiAttributionFromEntry(props.entry)
            ? CountUsersOnceState.Required
            : props.sectionProps.countUsersOnceState
        }
        chartType={props.sectionProps.chartType}
        dataGroupId={props.sectionProps.dataGroupId || dataGroupId}
        dataGroups={props.dataGroups}
        dateRange={props.sectionProps.dateRange as any}
        dateUnit={props.sectionProps.dateUnit}
        enableWorkspaceFilterRestrictions={props.sectionProps.enableWorkspaceFilterRestrictions}
        eventNames={
          !isFormulaEntry(queryMetricEntry) && queryMetricEntry.event?.value ? [queryMetricEntry.event.value] : []
        }
        includeCustomProperties={props.sectionProps.includeCustomProperties}
        showBorrowedProperties={props.sectionProps.showBorrowedProperties}
        hideEvents={!isEventBasedEntry(props.entry)}
        otherAggregationOptions={[
          ...(props.sectionProps.otherAggregationOptions || []),
          isRevenueEnabled && isUserBased && RECURRING_REVENUE_CHANGE,
        ].filter(Boolean)}
        hideEditControls={props.sectionProps.hideEditControls}
        projectHasPeople={props.sectionProps.projectHasPeople}
        projectId={props.sectionProps.projectId}
        readonly={props.entry.type === ResourceType.Cohort}
        selected={getSelectedCountBy(props.entry)}
        trackName={props.sectionProps.trackName}
        userId={props.sectionProps.userId}
        workspaceId={props.sectionProps.workspaceId}
        onSelect={(countBy) => {
          const {aggregationOperatorPerUser = null, property = null, advancedMenuValue = null} = countBy;
          const originalEntry = props.entry as QueryMetricEntry;
          let {rolling, cumulative, countUsersOnceType} = originalEntry;
          if (advancedMenuValue && isAdvancedValueCalculation(advancedMenuValue)) {
            // commited from advanced menu (changed calculation type)
            const calculationType = advancedMenuValue.calculationType;
            if (calculationType === CalculationTypes.Rolling) {
              rolling = {rollingWindowSize: advancedMenuValue.rollingWindowSize};
              cumulative = false;
            } else if (calculationType === CalculationTypes.Cumulative) {
              rolling = null;
              cumulative = true;
            }
          }

          if (Object.values(COUNT_USERS_ONCE_TYPE).some((type) => type === countBy.countUsersOnceType)) {
            countUsersOnceType = countBy.countUsersOnceType;
          }

          if (
            countBy.countType !== InsightsCountType.UniqueUsers ||
            countUsersOnceType === COUNT_USERS_ONCE_TYPE.MULTIPLE
          ) {
            countUsersOnceType = null;
          }

          if (
            isQueryUserEntry(originalEntry) &&
            property &&
            !(property.value === RECURRING_REVENUE_CHANGE.value || isScdPropertyName(property.value))
          ) {
            rolling = null;
            cumulative = false;
          }

          const entry: QueryEntry = {
            ...originalEntry,
            aggregationOperator: countBy.aggregationOperator,
            aggregationOperatorPerUser,
            property,
            rolling,
            cumulative: Boolean(cumulative),
            countUsersOnceType,
          };

          props.updateEntry(Action.Edit, entry);
          props.dispatchTrack(TrackingEvent.SelectNumericAggregator, {
            ...props.getEntryTrackingProps(entry),
            Aggregator: countBy.aggregationOperator,
            'Per-user aggregator': aggregationOperatorPerUser,
            'Rolling window size': rolling?.rollingWindowSize,
            Cumulative: Boolean(cumulative),
            ...(property && {
              'Property name': property.label,
              'Property data type': `type` in property && property.type,
            }),
          });
        }}
        onOpen={() => props.setActivePart(EntryPart.SubAggregator, `open`)}
        onClose={() => props.setActivePart(EntryPart.SubAggregator, `close`)}
        onTrack={preventTracking}
      />
      {selectedCalculation.calculationType && (
        <div className='calculation-selector'>
          <SelectReact
            key='calculation-selector'
            elref='calculationSelector'
            label={print(selectedCalculationLabel)}
            disableSearch
            menuWidth={180}
            trackName={props.sectionProps.trackName}
            theme={SelectTheme.Tertiary}
            disableTooltip
            onChange={(ev: CustomEvent) => {
              const calculation = ev.detail.item.value as Calculation;
              const originalEntry = props.entry as QueryMetricEntry;

              props.updateEntry(Action.Edit, {
                ...originalEntry,
                ...getQueryEntryUpdateFromCalculation({
                  originalEntry,
                  calculation,
                }),
              });
            }}
            onMenuChange={(ev) => props.onSelectMenuChange(ev, EntryPart.Calculation)}
            sections={calculationSelectorOptions}
          />
          <ButtonReact
            className='remove-button'
            theme={ButtonTheme.Tertiary}
            size={ButtonSize.Inline}
            type={ButtonType.Destructive}
            tooltip={{
              placement: Placement.RightCenter,
              size: DisplaySize.Small,
              text: t(`query-builder.actions.remove`),
            }}
            icon='x'
            onClick={() => {
              props.updateEntry(Action.Edit, {
                ...(props.entry as QueryMetricEntry),
                rolling: null,
                cumulative: null,
              });
            }}
          />
        </div>
      )}
    </>
  );
}

/**
 * Defines the property a given aggregation metric is working on
 */
export function RowNumericProperty(
  props: EntryRowProps<QueryMetricEntry> & {
    contextPanelParentElement: HTMLElement;
    isPlaceholder: boolean;
    handleSelect: (property) => void;
  },
) {
  const isUserBased = isUserBasedEntry(props.entry);
  const property = props.entry.property?.value ? props.entry.property : null;

  const type = (property as SectionItemProperty)?.joinPropertyType ?? property?.type ?? null;
  const icon = property
    ? getIconForPropertyType(type, {isCustom: `customProperty` in property, isScd: !!property.scdEvent})
    : `type-number`;

  return (
    <QueryBlockReact
      key='event-property'
      className={clsx({
        'is-placeholder': props.isPlaceholder,
      })}
      elref='propertyAggregationBlock'
      type={MenuType.Properties}
      open={props.isPlaceholder && props.activePart === EntryPart.NumericProperty}
      allDataGroups={props.sectionProps.dataGroupsEnabled}
      dataGroupId={props.sectionProps.dataGroupId || props.entry.dataGroupId}
      enableWorkspaceFilterRestrictions={props.sectionProps.enableWorkspaceFilterRestrictions}
      eventNames={!isFormulaEntry(props.entry) && props.entry.event?.value ? [props.entry.event.value] : []}
      includeCustomProperties={props.sectionProps.includeCustomProperties}
      showBorrowedProperties={props.sectionProps.showBorrowedProperties}
      hideEvents={!isEventBasedEntry(props.entry)}
      hideEditControls={props.sectionProps.hideEditControls}
      showSpecialDatePropertyUnit
      projectHasPeople={props.sectionProps.projectHasPeople && isUserBased}
      projectId={props.sectionProps.projectId}
      selected={property}
      selectorType={SelectorType.Button}
      selectorIcon={icon ?? `type-number`}
      theme={qbStyleAttrs(props.hasNewUI).selectorTheme}
      trackName={props.sectionProps.trackName}
      userId={props.sectionProps.userId}
      workspaceId={props.sectionProps.workspaceId}
      contextPanelParentElement={props.contextPanelParentElement}
      onSelect={(selection: SelectedPropertyItem) => props.handleSelect(selection.item)}
      onRemove={() => props.handleSelect(null)}
      onOpen={() => props.setActivePart(EntryPart.NumericProperty, `open`)}
      onClose={() => props.setActivePart(EntryPart.NumericProperty, `close`)}
      onTrack={preventTracking}
    />
  );
}

export function MultiAttributionAggregator(props: EntryRowProps) {
  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const {print} = useTranslation();

  const isOpen = props.activePart === EntryPart.MultiAttribution;
  const multiAttribution = getMultiAttributionFromEntry(props.entry);

  if (!(props.hasNewUI && (isOpen || multiAttribution))) {
    return null;
  }

  return (
    <div className='subitem-text multi-attribution-type'>
      <SelectReact
        className='subitem-select multi-attribution-type'
        elref='multiAttributionSelector'
        label={print(getMultiAttributionLabel(props.entry, isOpen))}
        title={isOpen ? `` : print(getMultiAttributionTitle(props.entry))}
        open={isOpen}
        theme={SelectTheme.QbSubitem}
        size={ButtonSize.Small}
        onMenuChange={(ev) => props.setActivePart(EntryPart.MultiAttribution, ev.detail.isOpen ? `open` : `close`)}
      >
        <div slot='body'>
          {isOpen && (
            <MultiAttributionMenu
              onCancel={() => props.setActivePart(EntryPart.MultiAttribution, `close`)}
              onChange={(selectedMultiAttribution) =>
                props.updateEntry(Action.Edit, getUpdatedMultiAttribution(props.entry, selectedMultiAttribution))
              }
              onDelete={() => props.updateEntry(Action.Edit, getUpdatedMultiAttribution(props.entry, null))}
              isSupportedMultiAttributionType={(type) =>
                isSupportedMultiAttributionTypeForEntry({
                  entry: props.entry,
                  multiAttributionType: type,
                  metricsResource: metrics,
                  behaviorsResource: behaviors,
                })
              }
              metricIndex={props.index}
              multiAttribution={multiAttribution || DefaultMultiAttribution}
              projectIsFreePlan={props.sectionProps.projectIsFreePlan}
            />
          )}
        </div>
      </SelectReact>
    </div>
  );
}
